import { apiClient, ApiError } from "@nabble/sdk";
import { clearSessionExpired, takeReturnTo } from "./returnTo";

export type AuthStep = "MFA" | "ENROLL_MFA" | "PRIVACY" | "DONE" | null;

export interface AuthState {
  authenticated: boolean;
  next: AuthStep;
  /** Opaque version identifier (the backend uses a date string); echoed back as-is to /api/auth/privacy. */
  privacyNoticeVersion: string | number | null;
  /** Where people write about their personal data (e.g. "privacidad@nabble.cl"). */
  privacyContact?: string | null;
  /** Minutes without server calls after which the backend ends the session. */
  sessionIdleMinutes?: number;
  /** Days offered by "Keep me signed in" on the login screen; 0 hides the option. */
  rememberMeDays?: number;
  /** When a "Keep me signed in" session ends (ISO instant); null for a normal session. */
  rememberedUntil?: string | null;
}

/** Body of every login step endpoint. `rememberedUntil` comes with DONE for a "Keep me signed in" login. */
export interface StepResponse {
  next: AuthStep;
  rememberedUntil?: string;
}

/** GET /api/auth/state — resumes a login in progress across page reloads. */
/**
 * Current auth state. An established session is `authenticated: true`; the
 * step is normalized to "DONE" so a page reload keeps the user signed in
 * (older backends answered `next: null` for an authenticated session).
 */
export async function fetchAuthState(): Promise<AuthState> {
  const state = await apiClient<AuthState>("/api/auth/state");
  return state.authenticated ? { ...state, next: "DONE" } : state;
}

export function pathForStep(step: AuthStep): string {
  switch (step) {
    case "MFA":
      return "/login/mfa";
    case "ENROLL_MFA":
      return "/login/enroll-mfa";
    case "PRIVACY":
      return "/login/privacy";
    case "DONE":
      return "/";
    default:
      return "/login";
  }
}

/**
 * Where to go after an auth step completes. When the flow reaches DONE, the
 * user returns to the page they were on before the session ended (if any).
 */
export function destinationAfterStep(step: AuthStep): string {
  if (step !== "DONE") return pathForStep(step);
  clearSessionExpired();
  return takeReturnTo() ?? "/";
}

export { ApiError };
